import { jev } from "./jev";
import type { BatchResult, Candidate, Judgement, Ranker, RerankRequest } from "./types";

function fnv1a(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** 데모용이지만 배지가 한쪽으로 몰리지 않도록 결정론적으로 퍼뜨린다. */
function fakeLogit(query: string, item: Candidate, pass: number): number {
  const unit = (fnv1a(`${query}|${item.id}|${pass}`) % 10_000) / 10_000;
  const z = -2.4 + unit * 6.8;
  // looksLikeSigmoid() 경고를 피하려고 [0,1] 구간을 비껴간다.
  // (실제 모델에서는 이 구간의 로짓도 정상이다.)
  return z >= 0 && z <= 1 ? z + 2.2 : z;
}

const delay = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });

/**
 * M1 UI 검증용 랭커. 실제 gate.worker.js와 같은 프로토콜(패스별 스트리밍)을 흉내낸다.
 * 지연값은 KURE FP16 실측 예상(8×128 ≈ 250~350ms)을 축소한 것이다.
 */
export class MockRanker implements Ranker {
  readonly backend = "mock";
  private readonly passDelay: Record<1 | 2 | 3, number> = { 1: 300, 2: 150, 3: 150 };

  async init(): Promise<void> {
    await delay(40, new AbortController().signal);
  }

  async *rerank(req: RerankRequest): AsyncIterable<BatchResult> {
    for (const { pass, items } of req.passes) {
      if (req.signal.aborted) return;
      const t0 = performance.now();
      await delay(this.passDelay[pass], req.signal);
      if (req.signal.aborted) return;

      const judgements: Judgement[] = items.map((item) => {
        const logit = fakeLogit(req.query, item, pass);
        const { pPass, pHold, pDrop, badge } = jev(logit);
        return { id: item.id, logit, pPass, pHold, pDrop, badge };
      });

      yield {
        epoch: req.epoch,
        pass,
        judgements,
        ms: Math.round(performance.now() - t0),
      };
    }
  }
}
